import { useEffect, useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type { AssignmentRole, DealDetailDto, DealListItemDto } from "@/types/deals";
import {
  useReassignA1,
  useReassignB1,
  useReassignC1,
} from "@/hooks/deals/useDeals";
import {
  useA1Picker,
  useB1Picker,
  useC1Picker,
} from "@/hooks/deals/useDealLookups";
import { dealLabel } from "./dealFormat";

interface Props {
  open: boolean;
  deal: DealListItemDto | DealDetailDto | null;
  /** Which assignment role to reassign. Bugfix sweep extended this to
   *  include C1 alongside B1 / A1 so the role-badges in the deal-detail
   *  header can drive reassignment for every seat, not just B1 / A1. */
  role: Extract<AssignmentRole, "C1" | "B1" | "A1">;
  onClose: () => void;
  onReassigned?: (deal: DealDetailDto) => void;
}

const MIN_REASON = 10;

/** Spec §5.7 — Reassign modal. ADMIN-only: the endpoints need Deals.Reassign
 *  (ADMIN alone holds it, owner ruling 2026-10-26) plus the C0, E1 or ADMIN
 *  role. The picker filters the shared /api/users/assignable list to the role
 *  (C1, B1 or A1); the reassignment reason is required by the server contract. */
export function ReassignModal({ open, deal, role, onClose, onReassigned }: Props) {
  const toast = useToast();
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // All three picker hooks must be called unconditionally to satisfy Rules
  // of Hooks even though only one applies. All three read the one cached
  // /api/users/assignable query (useAssignableUsers), so they make one request.
  const c1Picker = useC1Picker();
  const b1Picker = useB1Picker();
  const a1Picker = useA1Picker();
  const usersQuery = role === "C1" ? c1Picker : role === "B1" ? b1Picker : a1Picker;
  const reassignC1 = useReassignC1(deal?.id ?? "");
  const reassignB1 = useReassignB1(deal?.id ?? "");
  const reassignA1 = useReassignA1(deal?.id ?? "");
  const mutation = role === "C1" ? reassignC1 : role === "B1" ? reassignB1 : reassignA1;

  useEffect(() => {
    if (open) {
      setAssigneeUserId("");
      setReason("");
      setError(null);
    }
  }, [open]);

  if (!deal) return null;

  const currentUserId =
    role === "C1"
      ? deal.assignedC1Id
      : role === "B1"
        ? deal.assignedB1Id
        : deal.assignedA1Id;
  const candidates = (usersQuery.data ?? []).filter((u) => u.id !== currentUserId);
  // A deal with no C1 is given one through this dialog (owner ruling, 2026-10-09).
  const verb = currentUserId ? "Reassign" : "Assign";

  async function submit() {
    if (!assigneeUserId) {
      setError(`Select a ${role}.`);
      return;
    }
    if (reason.trim().length < MIN_REASON) {
      setError(`Reason must be at least ${MIN_REASON} characters.`);
      return;
    }
    setError(null);
    try {
      const result = await mutation.mutateAsync({
        assigneeUserId,
        reason: reason.trim(),
      });
      toast.success(`Reassigned ${dealLabel(deal!)}.`);
      onReassigned?.(result);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : "Reassignment failed.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${verb} ${role}`}
      description={`${deal.dealNumber} — ${dealLabel(deal)}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={mutation.isPending}>
            {verb}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-gray-mid">
          Assignment changes are auditable.{" "}
          {currentUserId
            ? `The new ${role} and the previous one are both notified in Phoenix.`
            : `The new ${role} is notified in Phoenix.`}
        </p>

        <FormField label={`New ${role}`} required>
          <Select
            value={assigneeUserId}
            onChange={(e) => setAssigneeUserId(e.target.value)}
            aria-label={`New ${role} assignee`}
            disabled={usersQuery.isLoading}
          >
            <option value="">
              {usersQuery.isLoading
                ? "Loading users…"
                : candidates.length === 0
                  ? `No ${role}s available`
                  : "Select an assignee…"}
            </option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Reason" required helperText={`Minimum ${MIN_REASON} characters.`}>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this being reassigned?"
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
